"use client";

import { useCallback, useEffect, useMemo } from "react";

import { MODELS } from "../constants";
import SingleSelect from "../SingleSelect";
import AutoModelIcon from "@/assets/models/auto-model-icon.webp";
import IdyllicBaseIcon from "@/assets/models/idyllic-base-icon.webp";
import IdyllicHDIcon from "@/assets/models/idyllic-hd-icon.webp";
import NSFW_FastIcon from "@/assets/models/nsfw-fast-icon.webp";
import Imagen4Icon from "@/assets/models/imagen-4-icon.webp";
import NSFW_Icon from "@/assets/models/nsfw-icon.webp";
import GPT_Icon from "@/assets/models/gpt-icon.webp";
import FluxProIcon from "@/assets/models/flux-pro-icon.webp";
import Imagen4UltraIcon from "@/assets/models/imagen-4-ultra-icon.webp";
import Grok2Icon from "@/assets/models/grok-2-icon.webp";

function getModelIcon(modelName: string) {
  switch (modelName) {
    case "auto":
      return AutoModelIcon.src;
    case "hd-turbo":
      return IdyllicBaseIcon.src;
    case "hd":
      return IdyllicHDIcon.src;
    case "nsfw-fast":
      return NSFW_FastIcon.src;
    case "imagen-4":
      return Imagen4Icon.src;
    case "nsfw":
      return NSFW_Icon.src;
    case "gpt":
      return GPT_Icon.src;
    case "flux-pro":
      return FluxProIcon.src;
    case "imagen-4-ultra":
      return Imagen4UltraIcon.src;
    case "grok-2":
      return Grok2Icon.src;
    default:
      return AutoModelIcon.src;
  }
}

interface ModelSelectProps {
  selectedModes: {
    isPublic: boolean;
    is18Plus: boolean;
    isTextImage: boolean;
  };
  aspectRatio: string;
  hasImages: boolean;
  selectedModel: string;
  setSelectedModel: (model: string) => void;
  isSubscribed: boolean;
}

const ModelSelect: React.FC<ModelSelectProps> = ({
  selectedModes,
  aspectRatio,
  hasImages,
  selectedModel,
  setSelectedModel,
  isSubscribed,
}) => {
  const selected = useMemo(() => {
    const model = MODELS.find((m) => m.name === selectedModel);
    return model
      ? {
          label: model.label,
          value: model.name,
          icon: (
            <img
              src={getModelIcon(model.name)}
              alt={model.label}
              className="tw:w-4 tw:h-4 tw:rounded-full tw:object-contain"
            />
          ),
        }
      : { label: "Auto", value: "auto" };
  }, [selectedModel]);

  const isModelDisabled = useCallback(
    (selected: string) => {
      const model = MODELS.find((m) => m.name === selected);
      if (!model) return false;

      if (!model.nsfw && selectedModes.is18Plus) return true;
      if (!model.imageText && selectedModes.isTextImage) return true;
      if (!model.includedAspectRatios.includes(aspectRatio)) return true;
      if (!model.imageEdits && hasImages) return true;

      return false;
    },
    [selectedModes, aspectRatio, hasImages]
  );

  const isModelLocked = (modelName: string) => {
    if (isSubscribed) return false;

    const model = MODELS.find((m) => m.name === modelName);
    return model ? model.premiumOnly : false;
  };

  useEffect(() => {
    if (isModelDisabled(selectedModel)) {
      setSelectedModel("auto");
    }
  }, [selectedModel, selectedModes]);

  return (
    <SingleSelect
      selected={selected}
      options={MODELS.map((model) => ({
        value: model.name,
        label: model.label,
        disabled: isModelDisabled(model.name),
        locked: isModelLocked(model.name),
        icon: (
          <img
            src={getModelIcon(model.name)}
            alt={model.label}
            className="tw:w-4 tw:h-4 tw:rounded-full tw:object-contain"
          />
        ),
      }))}
      onSelect={setSelectedModel}
      label="Model"
    />
  );
};

export default ModelSelect;
